import { Exception } from '@inter-ikea-kompis/exceptions';
import DateFormatter from '../date/DateFormatter.js';
import ProductPriceInformationSummaryTransformer from './ProductPriceInformationSummaryTransformer.js';
/**
 * Handles calculation of total price for a list of products.
 */
export default class TotalPriceCalculator {
    /**
     * Calculates total price.
     *
     * @param shoppingProducts Shopping products.
     * @param dateFormat Date format. Example: YYYY-MM-DD.
     * @param currencyCode Currency code to match with.
     * @param isCombinedProduct True if the products should be treated as one.
     * @returns Instance of TotalPriceSummary.
     */
    static calculate(shoppingProducts, dateFormat, currencyCode, isCombinedProduct) {
        if (!dateFormat) {
            throw new Exception('Second parameter "dateFormat" was not given.');
        }
        const totalPrice = {
            inclTax: 0,
            exclTax: 0,
            familyInclTax: 0,
            familyExclTax: 0,
            regularInclTax: 0,
            regularExclTax: 0,
            onlineSaleable: false,
            newLowerPrice: false,
            hasPreviousPrice: false,
            breathTakingItem: false,
            discontinued: false,
            family: false,
            temporary: false,
            validDateInterval: null,
            ecoPrice: 0,
            weeePrice: 0,
            currencyCode: currencyCode ?? null
        };
        const shoppingProductsWithQuantity = shoppingProducts.filter(({ quantity }) => quantity > 0);
        if (!shoppingProductsWithQuantity.length) {
            return totalPrice;
        }
        const validDates = [];
        totalPrice.newLowerPrice = isCombinedProduct ? false : true;
        totalPrice.breathTakingItem = true;
        totalPrice.onlineSaleable = true;
        totalPrice.discontinued = true;
        for (const shoppingProduct of shoppingProductsWithQuantity) {
            if (!shoppingProduct.product.valid) {
                throw new Error(`Failed to calculate total price. One of the products (${shoppingProduct.product.itemId}) is not valid.`);
            }
            if (shoppingProduct.product.content) {
                const product = shoppingProduct.product.content;
                if (!product.priceInformation) {
                    throw new Exception('Failed to calculate total price, priceInformation was not present in the content object.');
                }
                // Uses the currency provided or only the first currency in the list, if two currencies are present.
                const forcedCurrency = currencyCode
                    ? currencyCode
                    : product.priceInformation.salesPrice[0].currencyCode;
                const productPriceInformationSummary = ProductPriceInformationSummaryTransformer.transform(product.priceInformation, forcedCurrency);
                const familyValidDate = this.calculateFamilyPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                const troValidDate = this.calculateTroPrice(productPriceInformationSummary, totalPrice);
                this.calculateNormalPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                this.calculateRegularPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                this.calculateFeePrices(shoppingProduct, totalPrice, forcedCurrency);
                totalPrice.onlineSaleable =
                    product.priceInformation.onlineSaleable === false ? false : totalPrice.onlineSaleable;
                // If the products are considered a "combined" product it is enough that one of the products have NLP to produce a NPL price.
                // Otherwise all products need to have NLP to produce a total NLP price.
                if (isCombinedProduct) {
                    totalPrice.newLowerPrice =
                        productPriceInformationSummary.newLowerPrice === true ? true : totalPrice.newLowerPrice;
                }
                else {
                    totalPrice.newLowerPrice =
                        productPriceInformationSummary.newLowerPrice === false
                            ? false
                            : totalPrice.newLowerPrice;
                }
                totalPrice.hasPreviousPrice = productPriceInformationSummary.hasPreviousPrice
                    ? true
                    : totalPrice.hasPreviousPrice;
                totalPrice.discontinued =
                    productPriceInformationSummary.discontinued === false ? false : totalPrice.discontinued;
                totalPrice.breathTakingItem =
                    product.breathTakingItem === false ? false : totalPrice.breathTakingItem;
                if (familyValidDate) {
                    validDates.push(familyValidDate);
                }
                if (troValidDate) {
                    validDates.push(troValidDate);
                }
            }
        }
        totalPrice.validDateInterval = this.getValidDateInterval(validDates, dateFormat);
        if (!totalPrice.currencyCode) {
            totalPrice.currencyCode =
                shoppingProducts[0]?.product?.content?.priceInformation?.salesPrice[0]?.currencyCode ??
                    null;
        }
        return totalPrice;
    }
    /**
     * Calculates total prices for more than one currency. Used for dual currencies.
     *
     * @param shoppingProducts Shopping products.
     * @param dateFormat Date format. Example: YYYY-MM-DD.
     * @param isCombinedProduct True if the products should be treated as one.
     * @returns Instance of TotalPriceSummary.
     */
    static calculateMultiple(shoppingProducts, dateFormat, isCombinedProduct) {
        if (!dateFormat) {
            throw new Exception('Second parameter "dateFormat" was not given.');
        }
        if (shoppingProducts.some((shoppingProduct) => !shoppingProduct.product.content?.priceInformation)) {
            return [];
        }
        const totalPrices = [];
        if (!shoppingProducts.length) {
            totalPrices.push(this.calculate(shoppingProducts, dateFormat, undefined, isCombinedProduct));
            return totalPrices;
        }
        const providedCurrencies = [
            ...new Set(shoppingProducts[0]?.product?.content?.priceInformation?.salesPrice?.map((price) => price.currencyCode))
        ];
        for (const currency in providedCurrencies) {
            totalPrices.push(this.calculate(shoppingProducts, dateFormat, providedCurrencies[currency], isCombinedProduct));
        }
        return totalPrices;
    }
    /**
     * Handles normal price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     */
    static calculateNormalPrice(shoppingProduct, productPriceInformationSummary, totalPrice) {
        // We should always have a normal price
        if (productPriceInformationSummary.price) {
            totalPrice.inclTax +=
                productPriceInformationSummary.price.priceInclTax * shoppingProduct.quantity;
            totalPrice.exclTax +=
                productPriceInformationSummary.price.priceExclTax * shoppingProduct.quantity;
        }
        else if (productPriceInformationSummary.regularPrice) {
            totalPrice.inclTax +=
                productPriceInformationSummary.regularPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.exclTax +=
                productPriceInformationSummary.regularPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            throw new Exception(`Failed to calculate total price. Missing valid normal price in item: "${shoppingProduct.product.itemId}".`);
        }
    }
    /**
     * Handles family price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     * @returns Valid date.
     */
    static calculateFamilyPrice(shoppingProduct, productPriceInformationSummary, totalPrice) {
        if (productPriceInformationSummary.familyPrice) {
            totalPrice.family = true;
            totalPrice.familyInclTax +=
                productPriceInformationSummary.familyPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.familyExclTax +=
                productPriceInformationSummary.familyPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            // If this was not a family product, family price increases with normal price
            totalPrice.familyInclTax +=
                productPriceInformationSummary.price?.priceInclTax * shoppingProduct.quantity;
            totalPrice.familyExclTax +=
                productPriceInformationSummary.price?.priceExclTax * shoppingProduct.quantity;
        }
        if (productPriceInformationSummary.temporaryFamilyPrice) {
            totalPrice.temporary = true;
            return {
                validFrom: DateFormatter.isoStringToDate(productPriceInformationSummary.familyPrice?.validFromText),
                validTo: DateFormatter.isoStringToDate(productPriceInformationSummary.familyPrice?.validToText)
            };
        }
        return null;
    }
    /**
     * Handles TRO (Time Restricted Offer) price.
     *
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     * @returns Valid date.
     */
    static calculateTroPrice(productPriceInformationSummary, totalPrice) {
        if (productPriceInformationSummary.timeRestrictedOffer) {
            totalPrice.temporary = true;
            return {
                validFrom: DateFormatter.isoStringToDate(productPriceInformationSummary.price?.validFromText),
                validTo: DateFormatter.isoStringToDate(productPriceInformationSummary.price?.validToText)
            };
        }
        return null;
    }
    /**
     * Handles Regular price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     */
    static calculateRegularPrice(shoppingProduct, productPriceInformationSummary, totalPrice) {
        // There was a check here before: if (priceInformation.regularPrice && !priceInformation.newLowerPrice)
        // The check does not make sense, so it has been removed
        if (productPriceInformationSummary.regularPrice) {
            totalPrice.regularInclTax +=
                productPriceInformationSummary.regularPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.regularExclTax +=
                productPriceInformationSummary.regularPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            // If we don't have a special regular price, then the normal is regular
            totalPrice.regularInclTax +=
                productPriceInformationSummary.price?.priceInclTax * shoppingProduct.quantity;
            totalPrice.regularExclTax +=
                productPriceInformationSummary.price?.priceExclTax * shoppingProduct.quantity;
        }
    }
    /**
     * Handles fee price.
     *
     * @param shoppingProduct Shopping product.
     * @param totalPrice Total price model.
     * @param currencyCode To match if there are multiple (dual) currencies in the prices.
     */
    static calculateFeePrices(shoppingProduct, totalPrice, currencyCode) {
        const productFees = shoppingProduct.product.content?.priceInformation?.fees;
        if (productFees) {
            for (const fee of productFees.filter((fee) => fee.currencyCode === currencyCode)) {
                if (fee.typeCode === 'ECO') {
                    totalPrice.ecoPrice += fee.amount * shoppingProduct.quantity;
                }
                else if (fee.typeCode === 'WEEE') {
                    totalPrice.weeePrice += fee.amount * shoppingProduct.quantity;
                }
            }
        }
    }
    /**
     * Returns the shortest date span of multiple dates.
     *
     * @param validDates Valid dates.
     * @param dateFormat Pattern to use.
     * @returns Date interval.
     */
    static getValidDateInterval(validDates, dateFormat) {
        if (validDates.length > 0) {
            const date = {
                validFrom: validDates[0].validFrom,
                validTo: validDates[0].validTo
            };
            for (const validDate of validDates) {
                if (validDate.validFrom > date.validFrom) {
                    date.validFrom = validDate.validFrom;
                }
                if (validDate.validTo < date.validTo) {
                    date.validTo = validDate.validTo;
                }
            }
            return {
                validFrom: DateFormatter.dateToString(date.validFrom, dateFormat),
                validTo: DateFormatter.dateToString(date.validTo, dateFormat)
            };
        }
        return null;
    }
}
//# sourceMappingURL=TotalPriceCalculator.js.map